import React, { useState } from 'react';
import { useAuth, SignInButton, useUser } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import { useCreateConvexCheckout, useConvexSubscriptionSync, useOpenCustomerPortal } from '../hooks/useConvexPayments';

const PolarCheckout = ({ planType, planName, planPrice, planDescription, className, children, onPaymentSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { isSignedIn, isLoaded } = useAuth();
  const { user } = useUser();
  const createCheckout = useCreateConvexCheckout();
  const { mapped } = useConvexSubscriptionSync(false);
  const openPortal = useOpenCustomerPortal();

  const handleSubscribe = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!isLoaded) throw new Error('Authentication is loading...');
      if (!isSignedIn) {
        toast.error('Please sign in to continue with your subscription');
        setLoading(false);
        return;
      }

      // If user tries to buy yearly while having an active monthly sub, redirect to Polar Customer Portal for upgrade
      const hasActiveMonthly = mapped?.is_active && mapped?.plan_type === 'monthly';
      if (planType === 'yearly' && hasActiveMonthly) {
        toast.success('Opening customer portal to upgrade your plan...');
        await openPortal(window.location.origin + window.location.pathname + '?portal_return=true');
        return;
      }

      // Otherwise, proceed to create a checkout (new subscription or renewal)
      const res = await createCheckout(planType);
      
      // If backend indicates user already has active plan, try opening portal as a fallback
      if (!res?.success && typeof res?.error === 'string' && res.error.toLowerCase().includes('already have an active')) {
        toast('You already have an active subscription. Opening customer portal to change plans...');
        try {
          await openPortal(window.location.origin + window.location.pathname + '?portal_return=true');
          return;
        } catch (portalErr) {
          console.error('Portal open failed, staying on checkout flow:', portalErr);
          // continue below to surface original error
        }
      }
      if (res?.success) {
        if (res.isUpgrade) {
          toast.success(`🚀 Upgrading from ${res.previousPlan} to ${res.newPlan} plan! Redirecting to checkout...`);
        } else {
          toast.success('Redirecting to secure checkout...');
        }
        
        if (res.checkoutUrl) {
          window.location.href = res.checkoutUrl;
        } else {
          throw new Error('Failed to get checkout URL');
        }
      } else {
        // If the backend returns duplicate subscription message, fallback to portal
        const msg = res?.error || 'Failed to create checkout session';
        if (msg.toLowerCase().includes('already have an active')) {
          toast('You already have an active subscription. Opening customer portal to change plans...');
          await openPortal(window.location.origin + window.location.pathname + '?portal_return=true');
          return;
        }
        throw new Error(msg);
      }
    } catch (err) {
      console.error('Subscription error:', err);
      const errorMessage = err?.message ? String(err.message) : 'Failed to start subscription process';
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Enhanced children cloning to add click handler and loading state
  const enhancedChildren = React.Children.map(children, (child) => {
    if (React.isValidElement(child) && child.type === 'button') {
      return React.cloneElement(child, {
        onClick: handleSubscribe,
        disabled: loading || child.props.disabled,
        className: `${child.props.className || ''} ${loading ? 'opacity-50 cursor-not-allowed' : ''}`,
        children: loading ? (
          <div className="flex items-center justify-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
            <span>Processing...</span>
          </div>
        ) : child.props.children
      });
    }
    return child;
  });

  if (isLoaded && !isSignedIn) {
    return (
      <div className="subscription-plan">
        <SignInButton mode="modal">
          <div className="cursor-pointer">
            {enhancedChildren || (
              <button className={`${className} ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                {`Subscribe to ${planName}`}
              </button>
            )}
          </div>
        </SignInButton>
        <p className="text-sm text-gray-600 mt-2 text-center">
          Click to sign in and continue with subscription
        </p>
      </div>
    );
  }

  return (
    <div className="subscription-plan">
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {children ? enhancedChildren : (
        <button
          onClick={handleSubscribe}
          disabled={loading}
          className={`${className} ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {loading ? (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Processing...</span>
            </div>
          ) : (
            `Subscribe to ${planName}`
          )}
        </button>
      )}
    </div>
  );
};

export default PolarCheckout;